import type { SmsHistoryItem } from "@/modules/clients/types/clients.types";
import type {
	BalanceTransaction,
	SmsBalance,
	TopupInput,
	TransactionType,
} from "../types/sms.types";

const TIYINS_PER_SOUM = 100;
export const PRICE_PER_SMS_SOUM = 0.5; // 50 tiyins
export const DEFAULT_WARNING_THRESHOLD_SOUM = 500; // 500 UZS

export type BackendSmsBalance = {
	balance: number; // in tiyin
	totalToppedUp: number; // in tiyin
	totalSpent: number; // in tiyin;
};

export type BackendBalanceTransaction = {
	id: string;
	type: "topup" | "spend" | "refund";
	amount_uzs: number;
	balance_before_uzs: number;
	balance_after_uzs: number;
	description: string | null;
	payment_method: string | null;
	payment_ref: string | null;
	created_by: string | null;
	created_at: string;
};

export type BackendBalanceTransactionsResponse = {
	items: BackendBalanceTransaction[];
	meta: {
		total: number;
		total_topped_up_uzs: number;
		total_spent_uzs: number;
	};
};

export type BackendSmsLog = {
	id: string;
	orgId: string;
	clientId: string;
	saleId: string | null;
	scheduleId: string | null;
	templateId: string | null;
	dispatchId: string | null;
	sentBy: string | null;
	phoneNumber: string;
	messageBody: string;
	triggerType: "auto" | "manual";
	status: "pending" | "sent" | "delivered" | "failed";
	errorMessage: string | null;
	partsCount: number | null;
	pricePerPart: number | null; // in tiyin
	totalPrice: number | null; // in tiyin
	createdAt: string;
};

export type BackendSmsTemplate = {
	id: string;
	orgId: string;
	name: string;
	code: string;
	body: string;
	type: "payment_reminder" | "overdue" | "welcome" | "custom";
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
};

export type BackendSmsAutoSettings = {
	id: string;
	orgId: string;
	isEnabled: boolean;
	daysBefore: number[];
	daysAfter: number[];
	reminderTemplateId: string | null;
	overdueTemplateId: string | null;
	createdAt: string;
	updatedAt: string;
};

// ── Reports ──────────────────────────────────────────────────────────────────

export type BackendSmsReport = {
	totalSent: number;
	totalDelivered: number;
	totalFailed: number;
	totalCost: number; // in tiyin
	period: "day" | "week" | "month";
};

export type SmsReportMapped = {
	totalSent: number;
	totalDelivered: number;
	totalFailed: number;
	totalCostUzs: number;
	deliveryRate: number; // percent
	period: "day" | "week" | "month";
};

export function mapBackendReport(raw: BackendSmsReport): SmsReportMapped {
	return {
		totalSent: raw.totalSent,
		totalDelivered: raw.totalDelivered,
		totalFailed: raw.totalFailed,
		totalCostUzs: raw.totalCost / TIYINS_PER_SOUM,
		deliveryRate:
			raw.totalSent > 0 ? Math.round((raw.totalDelivered / raw.totalSent) * 1000) / 10 : 0,
		period: raw.period,
	};
}

// ── Detalization ─────────────────────────────────────────────────────────────

export type BackendSmsDetalizationItem = BackendSmsLog;

export type SmsDetalizationItemMapped = {
	id: string;
	phoneNumber: string;
	messageBody: string;
	triggerType: "auto" | "manual";
	status: "pending" | "sent" | "delivered" | "failed";
	errorMessage: string | null;
	partsCount: number;
	pricePerPartUzs: number;
	totalPriceUzs: number;
	createdAt: string;
};

export function mapBackendDetalizationItem(
	item: BackendSmsDetalizationItem
): SmsDetalizationItemMapped {
	return {
		id: item.id,
		phoneNumber: item.phoneNumber,
		messageBody: item.messageBody,
		triggerType: item.triggerType,
		status: item.status,
		errorMessage: item.errorMessage,
		partsCount: item.partsCount ?? 1,
		pricePerPartUzs: (item.pricePerPart ?? 50) / TIYINS_PER_SOUM,
		totalPriceUzs: (item.totalPrice ?? 50) / TIYINS_PER_SOUM,
		createdAt: item.createdAt,
	};
}

export function mapBackendBalance(data: BackendSmsBalance): SmsBalance {
	return {
		currentBalance: data.balance / TIYINS_PER_SOUM,
		totalTopup: data.totalToppedUp / TIYINS_PER_SOUM,
		totalSpent: data.totalSpent / TIYINS_PER_SOUM,
		warningThreshold: DEFAULT_WARNING_THRESHOLD_SOUM,
		pricePerSms: PRICE_PER_SMS_SOUM,
	};
}

export function toTopUpRequest(input: TopupInput) {
	return {
		amount: Math.round(input.amount * TIYINS_PER_SOUM),
		paymentMethod: input.method,
		paymentRef: input.documentNumber || undefined,
		description: input.note || undefined,
	};
}

export function mapBackendTransaction(tx: BackendBalanceTransaction): BalanceTransaction {
	const date = new Date(tx.created_at);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const dateStr = `${year}-${month}-${day} ${hours}:${minutes}`;

	const typeMap: Record<string, TransactionType> = {
		topup: "topup",
		spend: "spend",
		refund: "topup",
	};

	const amountUzs = tx.amount_uzs / TIYINS_PER_SOUM;
	const isSpend = tx.type === "spend";

	return {
		id: tx.id,
		date: dateStr,
		type: typeMap[tx.type] ?? "spend",
		amount: isSpend ? -amountUzs : amountUzs,
		balanceAfter: tx.balance_after_uzs / TIYINS_PER_SOUM,
		note:
			tx.description ??
			(isSpend ? "SMS xarajat" : tx.type === "refund" ? "Qaytarildi" : "Balans to'ldirildi"),
	};
}

export function mapBackendLogToTx(log: BackendSmsLog): BalanceTransaction {
	const date = new Date(log.createdAt);
	// Format to: YYYY-MM-DD HH:mm
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const dateStr = `${year}-${month}-${day} ${hours}:${minutes}`;

	const amountUzs = (log.totalPrice ?? 50) / TIYINS_PER_SOUM;

	return {
		id: log.id,
		date: dateStr,
		type: "spend",
		amount: -amountUzs,
		balanceAfter: 0, // Not tracked per log in backend logs list
		note: `SMS: ${log.phoneNumber}${log.errorMessage ? ` · Xatolik: ${log.errorMessage}` : ""}`,
	};
}

export function mapBackendLogToHistoryItem(log: BackendSmsLog): SmsHistoryItem {
	const date = new Date(log.createdAt);
	// Format to: YYYY-MM-DD HH:mm:ss
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	const seconds = String(date.getSeconds()).padStart(2, "0");
	const dateStr = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;

	let status: SmsHistoryItem["status"] = "pending";
	if (log.status === "delivered") {
		status = "delivered";
	} else if (log.status === "failed") {
		status = "failed";
	}

	return {
		time: dateStr,
		message: log.messageBody,
		status,
	};
}
